import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Dropdown, Form } from "react-bootstrap";
import Select from "react-select";
import { getSelectProps } from "utils/helperFunctions";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getEppActivityTableData,
  selectAllEppActivityTableData,
  selectGetEppActivityTableDataStatus,
} from "../slice";
import SortableTable from "app/components/SortableTable";
import ExportCSVButton from "app/storybookComponents/Button/ExportCSVButton";
import { trackCSVExport } from "utils/trackingFunctions";
import { TableHeader } from "app/components/SortableTable/types";
import {
  EppEntityType,
  EppUserActivityRow,
  EppEntityActivityRow,
  EppUserResponse,
} from "../types";
import EmptyCard from "app/storybookComponents/Cards/EmptyCard";
import TableDropdownMenu from "app/components/Dropdowns/TableDropdownMenu";
import {
  EppEntityTypes,
  EPP_TRAIT_KEYS,
  EPP_USER_CSV_TABLE_HEADERS,
  EPP_ENTITY_CSV_TABLE_HEADERS,
} from "../constants";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

const WorkplaceInsightsTable = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // ---------------------------- Selectors ----------------------------
  const allEppActivityTableData = useAppSelector(selectAllEppActivityTableData);
  const getEppActivityTableDataStatus = useAppSelector(
    selectGetEppActivityTableDataStatus
  );

  // ---------------------------- States ----------------------------
  const [entitySelected, setEntitySelected] = useState<EppEntityType>("user");
  const [displayedInput, setDisplayedInput] = useState<string>("");

  // ---------------------------- Effect ----------------------------
  useEffect(() => {
    dispatch(
      getEppActivityTableData({
        entityType: entitySelected,
      })
    );
  }, [dispatch, entitySelected]);

  const isTableLoading = getEppActivityTableDataStatus === "loading";

  // ---------------------------- Data Access ----------------------------
  const rawData = allEppActivityTableData?.[entitySelected];

  const userRows: EppUserActivityRow[] = useMemo(() => {
    if (entitySelected !== "user" || !rawData) return [];
    return (rawData as EppUserResponse).users ?? [];
  }, [entitySelected, rawData]);

  const entityRows: EppEntityActivityRow[] = useMemo(() => {
    if (entitySelected === "user" || !rawData) return [];
    return (rawData as EppEntityActivityRow[]) ?? [];
  }, [entitySelected, rawData]);

  // ---------------------------- Filtering ----------------------------
  const getFilteredUserRows = useCallback((): EppUserActivityRow[] => {
    if (!displayedInput) return userRows;
    const lowerInput = displayedInput.toLowerCase();
    return userRows.filter((row) => {
      const fullName = `${row.firstName ?? ""} ${row.lastName ?? ""}`
        .trim()
        .toLowerCase();
      return fullName.includes(lowerInput);
    });
  }, [displayedInput, userRows]);

  const getFilteredEntityRows = useCallback((): EppEntityActivityRow[] => {
    if (!displayedInput) return entityRows;
    const lowerInput = displayedInput.toLowerCase();
    return entityRows.filter((row) =>
      (row.name ?? "").toLowerCase().includes(lowerInput)
    );
  }, [displayedInput, entityRows]);

  // ---------------------------- Navigation ----------------------------
  const onUserClick = (userAccountId: number) => {
    navigate(`/UserGuide/${userAccountId}?tab=Workplace+Insights`);
  };

  const onTeamClick = (teamId: number) => {
    navigate(`/TeamGuide/${teamId}?tab=Team+Personality`);
  };

  // ---------------------------- Table Row Builders ----------------------------
  const getUserTableRow = (row: EppUserActivityRow) => {
    const { userAccountId, firstName, lastName, traitScores } = row;
    const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();

    const traitCells: Record<
      string,
      { displayValue: string; sortValue: number }
    > = {};
    for (const trait of EPP_TRAIT_KEYS) {
      const value = traitScores?.[trait];
      traitCells[trait] = {
        displayValue: value != null ? String(value) : "-",
        sortValue: value ?? -1,
      };
    }

    return {
      name: {
        sortValue: fullName,
        displayValue: (
          <button
            className="button-link"
            onClick={() => onUserClick(userAccountId)}
          >
            <p className="user-name-cell__name">{fullName}</p>
          </button>
        ),
      },
      dateEventTaken: row.dateEventTaken
        ? new Date(row.dateEventTaken).toLocaleDateString("en-US")
        : "-",
      ...traitCells,
    };
  };

  const getEntityTableRow = (row: EppEntityActivityRow) => {
    const {
      id,
      name,
      totalMembers,
      membersWithEpp,
      completionRate,
      traitScores,
    } = row;

    const completionPercentage = Math.round(completionRate * 100);

    const traitCells: Record<
      string,
      { displayValue: string; sortValue: number }
    > = {};
    for (const trait of EPP_TRAIT_KEYS) {
      const value = traitScores?.[trait];
      traitCells[trait] = {
        displayValue: value != null ? String(value) : "-",
        sortValue: value ?? -1,
      };
    }

    const nameCell =
      entitySelected === "team" ? (
        <button className="button-link" onClick={() => onTeamClick(id)}>
          <p className="user-name-cell__name">{name}</p>
        </button>
      ) : (
        <p>{name}</p>
      );

    return {
      name: {
        sortValue: name,
        displayValue: nameCell,
      },
      membersWithEpp: membersWithEpp,
      totalMembers: totalMembers,
      completionRate: {
        sortValue: completionPercentage,
        displayValue: `${completionPercentage}% (${membersWithEpp}/${totalMembers})`,
      },
      ...traitCells,
    };
  };

  // ---------------------------- Headers ----------------------------
  const getHeaders = (): TableHeader[] => {
    const traitHeaders: TableHeader[] = EPP_TRAIT_KEYS.map((trait) => ({
      label: trait,
      key: trait,
    }));

    if (entitySelected === "user") {
      return [
        { label: "Name", key: "name" },
        { label: "Date Taken", key: "dateEventTaken", isDate: true },
        ...traitHeaders,
      ];
    }

    return [
      { label: "Name", key: "name" },
      { label: "Members with EPP", key: "membersWithEpp" },
      { label: "Total Members", key: "totalMembers" },
      { label: "Completion Rate", key: "completionRate", sortInverse: true },
      ...traitHeaders,
    ];
  };

  // ---------------------------- CSV ----------------------------
  const getCSVExportRows = useCallback(() => {
    trackCSVExport("Workplace Insights Activity");

    if (entitySelected === "user") {
      return getFilteredUserRows().map((row) => {
        const baseRow: Record<string, string> = {
          firstName: row.firstName ?? "",
          lastName: row.lastName ?? "",
          dateEventTaken: row.dateEventTaken
            ? new Date(row.dateEventTaken).toLocaleDateString("en-US")
            : "",
        };
        for (const trait of EPP_TRAIT_KEYS) {
          baseRow[trait] =
            row.traitScores?.[trait] != null
              ? String(row.traitScores[trait])
              : "";
        }
        return baseRow;
      });
    }

    return getFilteredEntityRows().map((row) => {
      const completionPercentage = Math.round(row.completionRate * 100);
      const baseRow: Record<string, string> = {
        name: row.name ?? "",
        membersWithEpp: String(row.membersWithEpp),
        totalMembers: String(row.totalMembers),
        completionRate: `${completionPercentage}%`,
      };
      for (const trait of EPP_TRAIT_KEYS) {
        baseRow[trait] =
          row.traitScores?.[trait] != null
            ? String(row.traitScores[trait])
            : "";
      }
      return baseRow;
    });
  }, [entitySelected, getFilteredUserRows, getFilteredEntityRows]);

  // ---------------------------- Dropdown Helpers ----------------------------
  const getDropdownFromRecord = (
    record: Record<string, string>,
    selected: string,
    onSelect: (str: string | null) => void,
    isDisabled: boolean = false
  ) => (
    <Dropdown onSelect={onSelect}>
      <Dropdown.Toggle
        id="dropdown-basic"
        className="d-flex align-items-center justify-content-between"
        style={{ width: "200px" }}
        variant="light"
        disabled={isDisabled}
      >
        {record[selected]}
      </Dropdown.Toggle>
      <TableDropdownMenu>
        {Object.entries(record).map(([key, value], index) => (
          <div key={key}>
            <Dropdown.Item eventKey={key}>{value}</Dropdown.Item>
            {index !== Object.keys(record).length - 1 && <Dropdown.Divider />}
          </div>
        ))}
      </TableDropdownMenu>
    </Dropdown>
  );

  // ---------------------------- Search ----------------------------
  const getSearchPlaceholder = () => {
    switch (entitySelected) {
      case "team":
        return "Search for a team...";
      case "department":
        return "Search for a department...";
      default:
        return "Search for a user...";
    }
  };

  const getSearchInput = () => {
    const { selectStyles, components } = getSelectProps();
    return (
      <Form.Group className="min-w-[300px]">
        <Select
          placeholder={getSearchPlaceholder()}
          isClearable={true}
          isSearchable={true}
          components={components}
          inputValue={displayedInput}
          styles={selectStyles}
          menuIsOpen={false}
          onInputChange={(e, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setDisplayedInput(e);
            }
          }}
        />
      </Form.Group>
    );
  };

  // ---------------------------- Render Sections ----------------------------
  const rows =
    entitySelected === "user"
      ? getFilteredUserRows().map(getUserTableRow)
      : getFilteredEntityRows().map(getEntityTableRow);

  const headers = getHeaders();

  const csvHeaders =
    entitySelected === "user"
      ? EPP_USER_CSV_TABLE_HEADERS
      : EPP_ENTITY_CSV_TABLE_HEADERS;

  const totalDataLength =
    entitySelected === "user" ? userRows.length : entityRows.length;

  const getEmptyState = () => {
    if (isTableLoading) return null;

    if (totalDataLength === 0) {
      return (
        <EmptyCard
          title="No Workplace Insights activity found"
          bodyText="Once there is Workplace Insights activity, it will be shown here."
        />
      );
    }

    if (rows.length === 0) {
      return (
        <EmptyCard
          title={`No ${entitySelected} found matching "${displayedInput}"`}
          bodyText="Try searching for a different name."
        />
      );
    }

    return null;
  };

  return (
    <Card>
      <div className="column-gap-8px">
        <h2>Workplace Insights Activity</h2>
        <p>Showing EPP activity in this entire organization</p>
      </div>
      {entitySelected !== "user" && (
        <div className="warning-banner lighter-blue flex">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1" />
          <div>
            <p className="fw-bold">
              Team and Department views show average EPP scores across all
              members who have completed the assessment.
            </p>
            <p>
              Switch to User view to see individual scores. Export CSV includes
              all dimensions for all users.
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {getSearchInput()}
        <div className="d-flex justify-content-between align-items-center flex-grow">
          <div className="row-gap-12px">
            {getDropdownFromRecord(
              EppEntityTypes,
              entitySelected,
              (e) => {
                setEntitySelected((e ?? "user") as EppEntityType);
                setDisplayedInput("");
              },
              isTableLoading
            )}
          </div>
          <div>
            <ExportCSVButton
              headers={csvHeaders}
              getRows={getCSVExportRows}
              fileName="Workplace Insights Activity"
              disabled={isTableLoading}
            />
          </div>
        </div>
      </div>
      <SortableTable
        rows={rows}
        tableClassName="admin-console-table"
        columnHeaders={[...headers]}
        isLoading={isTableLoading}
      />
      {getEmptyState()}
    </Card>
  );
};

export default WorkplaceInsightsTable;
